import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Avatar,
  CircularProgress,
  Stack,
  Typography,
  Card,
  CardContent,
  Link,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import BoxHeader from "../atoms/BoxHeader";
import Navbar from "./Navbar";
import { AuthContext } from "../../Auth/Context";
import stylesReports from "../../styles/Organisms/OpcionesReportes/reports";
import Searching from "../molecules/Searching";
import stylesDashboard from "../../styles/Organisms/OpcionDashboard/dasboard";
import { useTranslation } from "react-i18next";
import "../../Traduccion_ES_EN/i18nMiCuenta";
import useWexClient from "../../hooks/Clients/wexClient,";
import TableLine from "../atoms/TableLine";
import CardComponent from "../atoms/CardComponent";
import Modal from "../atoms/ModalComponent";
import { id } from "date-fns/locale";
import AlertComponent from "../atoms/Alert";

function OptionSearch() {
  const { payload } = useContext(AuthContext);
  const {
    getServices,
    getFolioAdjusters,
    getConversationByHeader,
    imgForIncident,
    getResolution,
  } = useWexClient();

  const { t } = useTranslation();

  const [tableData, setTableData] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // State for services
  const [service, setService] = useState([]);
  const [image, setImage] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");

  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // State for survey modal
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyData, setSurveyData] = useState([]);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);

  // State for images modal
  const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // State for alerts
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");
  const alertTimeoutRef = useRef(null);

  const clearAlertTimeout = () => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = null;
    }
  };

  const hideAlert = () => {
    clearAlertTimeout();
    setShowAlert(false);
  };

  const showTemporaryAlert = (message, type, onTimeout) => {
    clearAlertTimeout();
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    alertTimeoutRef.current = setTimeout(() => {
      setShowAlert(false);
      alertTimeoutRef.current = null;
      onTimeout?.();
    }, 3000);
  };

  useEffect(() => () => clearAlertTimeout(), []);

  const columnTitles = [
    "Folio",
    "External ID",
    "Ajustador",
    "Status",
    "Tiempo de Aceptación",
    "Tiempo en llegar al siniestro",
    "Tiempo de Resolución",
    "Tiempo en sitio",
    "Clasificación",
    "Comentarios",
    "Acciones",
  ];

  const handleCopyRow = (item) => {
    const dataToCopy = [
      { label: "External ID", value: item.externalFolio },
      { label: "Ajustador", value: item.agent?.name || "N/A" },
      { label: "Status", value: statusMapping(item.status?.code) || "N/A" },
      {
        label: "Tiempo de Aceptación",
        value: item.kpis?.assignTime?.text || "N/A",
      },
      {
        label: "Tiempo en llegar al siniestro",
        value:
          item.kpis?.travelTime?.text ?? formatMs(item.kpis?.travelTime?.ms),
      },
      {
        label: "Tiempo de Resolución",
        value: item.kpis?.resolutionTime?.text || "N/A",
      },
      {
        label: "Tiempo en sitio",
        value:
          item.kpis?.onSiteTime?.text ?? formatMs(item.kpis?.onSiteTime?.ms),
      },
      {
        label: "Clasificación",
        value: item.classification?.[0]?.type || "N/A",
      },
      {
        label: "Comentarios",
        value: item.classification?.[0]?.comments || "N/A",
      },
    ];

    const textToCopy = dataToCopy
      .map((row) => `${row.label}: ${row.value}`)
      .join("\n");

    navigator.clipboard.writeText(textToCopy).then(() => {
      showTemporaryAlert(
        "Datos del folio copiados al portapapeles",
        "success",
      );
    });
  };

  const cellComponent = (item, title) => {
    switch (title) {
      case "Folio":
        return (
          <Link
            onClick={() => getImages(item.idHeader)}
            sx={{
              cursor: "pointer",
              color: "#1976d2",
              textDecoration: "underline",
              "&:hover": {
                textDecoration: "underline",
                color: "#1565c0",
              },
            }}
          >
            {item.folio}
          </Link>
        );
      case "External ID":
        return item.externalFolio;
      case "Ajustador":
        return item.agent?.name || "N/A";
      case "Status":
        return statusMapping(item.status?.code) || "N/A";
      case "Tiempo de Aceptación":
        return item.kpis?.assignTime?.text || "N/A";
      case "Tiempo en llegar al siniestro":
        return (
          item.kpis?.travelTime?.text ?? formatMs(item.kpis?.travelTime?.ms)
        );
      case "Tiempo de Resolución":
        return item.kpis?.resolutionTime?.text || "N/A";
      case "Tiempo en sitio":
        return (
          item.kpis?.onSiteTime?.text ?? formatMs(item.kpis?.onSiteTime?.ms)
        );
      case "Clasificación":
        return item.classification?.[0]?.type || "N/A";
      case "Comentarios":
        return item.classification?.[0]?.comments || "N/A";
      case "Acciones":
        return (
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Link
              onClick={() => handleViewConversation(item)}
              sx={{ cursor: "pointer", fontSize: "0.85rem" }}
            >
              Ver conversación
            </Link>
            <Link
              onClick={() => handleViewSurvey(item)}
              sx={{ cursor: "pointer", fontSize: "0.85rem" }}
            >
              Ver Encuesta
            </Link>
            <Tooltip title="Copiar Datos">
              <IconButton
                onClick={() => handleCopyRow(item)}
                sx={{ color: "#4caf50", padding: "2px" }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return "";
    }
  };

  const statusMapping = (status) => {
    switch (status) {
      case 1:
        return "En camino";
      case 4:
        return "Finalizado";
      case 40:
        return "Rechazado";
      case 50:
        return "No tomado";
      default:
        return "Desconocido";
    }
  };

  const getImages = async (idHeader) => {
    hideAlert();
    setIsModalOpen(false);
    setIsSurveyModalOpen(false);

    try {
      setIsImagesModalOpen(true);
      setIsLoadingImages(true);
      setImages([]);

      const data = await imgForIncident(idHeader);

      if (data?.success === true && data?.data) {
        setImages(data.data);
      } else {
        setImages([]);
        showTemporaryAlert(
          data?.message || "Error al obtener las imágenes",
          "error",
          () => setIsImagesModalOpen(false),
        );
      }
    } catch (error) {
      console.log("Error:", error);
      setImages([]);
      showTemporaryAlert(
        "Error de red al obtener las imágenes",
        "error",
        () => setIsImagesModalOpen(false),
      );
    } finally {
      setIsLoadingImages(false);
    }
  };

  useEffect(() => {
    const fetchDataFromAPI = async () => {
      try {
        const apiData = await getServices(payload._id);

        const serviceData = apiData.map((item) => ({
          id: item._id,
          name: item.name,
        }));
        const imageData = apiData.map((item) => item._config.avatarClient);
        setService(serviceData);
        setImage(imageData);
        const initialOption = serviceData.length > 0 ? serviceData[0].id : "";
        setSelectedOption(initialOption);
      } catch (error) {
        // console.error(error);
      }
    };

    fetchDataFromAPI();
  }, [payload._id]);

  const selectedPosition = service.findIndex(
    (option) => option.id === selectedOption,
  );

  const selectedImage = selectedPosition !== -1 ? image[selectedPosition] : "";
  const selectedService = service.find(
    (option) => option.id === selectedOption,
  );
  const serviceName = selectedService
    ? selectedService.name
    : t("Welcome.text60");

  const formatMs = (ms) => {
    if (!ms) return "N/A";
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const handleUpdateTableData = async (externalFolio) => {
    setIsSearching(true);
    setTableData([]);
    hideAlert();

    try {
      const body = {
        externalFolio,
        serviceId: payload.service,
      };
      const data = await getFolioAdjusters(body);

      if (data?.success === false) {
        showTemporaryAlert(data?.message || "Error en la búsqueda", "error");
        setTableData([]);
        return;
      }

      if (data?.success === true && data?.data) {
        const normalized = Array.isArray(data.data) ? data.data : [data.data];

        setTableData(normalized);
      }
    } catch (error) {
      showTemporaryAlert("Error de red al realizar la búsqueda", "error");
      setTableData([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleViewConversation = async (item) => {
    hideAlert();
    setIsSurveyModalOpen(false);
    setIsImagesModalOpen(false);
    setIsModalOpen(true);
    setIsLoadingConversation(true);
    setConversation([]);

    try {
      const body = {
        idHeader: item.idHeader,
        type: "_WHATSAPP_CABIN_",
      };
      const response = await getConversationByHeader(body);

      if (response?.success === true && response?.data) {
        setConversation(response.data);
      } else {
        showTemporaryAlert(
          response?.message || "Error al obtener la conversación",
          "error",
          () => setIsModalOpen(false),
        );
      }
    } catch (error) {
      showTemporaryAlert(
        "Error de red al obtener la conversación",
        "error",
        () => setIsModalOpen(false),
      );
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const handleViewSurvey = async (item) => {
    hideAlert();
    setIsModalOpen(false);
    setIsImagesModalOpen(false);
    setIsSurveyModalOpen(true);
    setIsLoadingSurvey(true);
    setSurveyData([]);

    try {
      const visitId = item.incident?.id || item.idHeader;
      const response = await getResolution(visitId);

      if (response?.success === true && response?.data) {
        const formattedSurvey = [];
        const data = response.data;

        if (data.answers && Array.isArray(data.answers)) {
          data.answers.forEach((entry) => {
            // Question from Agent
            if (entry.question?.label) {
              formattedSurvey.push({
                type: "agent",
                msg: entry.question.label,
                dateSend: data.createdAt,
              });
            }
            // Answer from Client/User
            if (entry.value) {
              formattedSurvey.push({
                type: "client",
                msg: entry.value,
                dateSend: data.createdAt,
              });
            }
          });
        }
        setSurveyData(formattedSurvey);
      } else {
        showTemporaryAlert(
          response?.message || "Error al obtener la encuesta",
          "error",
          () => setIsSurveyModalOpen(false),
        );
      }
    } catch (error) {
      showTemporaryAlert(
        "Error de red al obtener la encuesta",
        "error",
        () => setIsSurveyModalOpen(false),
      );
    } finally {
      setIsLoadingSurvey(false);
    }
  };

  const handleCopySurvey = () => {
    if (!surveyData || surveyData.length === 0) return;

    const textToCopy = surveyData
      .map((msg) => {
        const role = msg.type === "agent" ? "Pregunta" : "Respuesta";
        return `${role}: ${msg.msg}`;
      })
      .join("\n");

    navigator.clipboard.writeText(textToCopy).then(() => {
      showTemporaryAlert("Encuesta copiada al portapapeles", "success");
    });
  };

  return (
    <Stack
      sx={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "white",
      }}
    >
      <Stack sx={{ width: "100%" }}>
        <Navbar />
      </Stack>
      <Stack
        sx={{
          ...stylesReports.color,
          width: "100vw",
          alignItems: "center",
        }}
      >
        <BoxHeader>
          <Stack
            direction="row"
            sx={{
              width: "100%",
              height: "22.3rem",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 1rem",
              gap: "2rem", // Adding gap for spacing between sections
            }}
          >
            <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
              <div
                style={{
                  ...stylesDashboard.divb,
                  width: "8rem",
                  height: "8rem",
                  borderRadius: "35rem",
                }}
              >
                {selectedImage ? (
                  <Avatar
                    src={selectedImage}
                    sx={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                ) : null}
              </div>
              <Typography
                variant="h4"
                sx={{
                  ...stylesDashboard.title,
                  marginLeft: "1rem",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  height: "8rem",
                }}
              >
                {serviceName}
              </Typography>
            </Stack>

            <Stack sx={{ width: "35%" }}>
              <Searching onUpdateTableData={handleUpdateTableData} />
            </Stack>
          </Stack>
        </BoxHeader>
      </Stack>
      <Stack sx={{ alignItems: "center", width: "100%", padding: "2rem" }}>
        {isSearching ? (
          <CircularProgress color="inherit" />
        ) : (
          <CardComponent>
            <TableLine
              data={tableData}
              columnTitles={columnTitles}
              cellComponent={cellComponent}
              showActionColumn={false}
            />
          </CardComponent>
        )}
      </Stack>

      {isModalOpen && (
        <Modal
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Conversación del Folio"
          width="50vw"
          height="80vh"
          sx={{
            overflowY: "auto",
            padding: "1rem",
          }}
        >
          {showAlert && (
            <Box sx={{ width: "100%", marginBottom: "1rem" }}>
              <AlertComponent
                message={alertMessage}
                typeAlert={alertType}
                handleClose={hideAlert}
              />
            </Box>
          )}
          {isLoadingConversation ? (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="inherit" />
            </Stack>
          ) : conversation.length > 0 ? (
            conversation.map((msg, index) => {
              const isAgent = msg.type === "agent";

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: isAgent ? "flex-start" : "flex-end",
                    width: "100%",
                    position: "relative",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "fit-content",
                      maxWidth: "75%",
                      padding: "0.8rem 1rem",
                      borderRadius: "18px",
                      backgroundColor: isAgent ? "#ffffff" : "#dcf8c6",
                      color: "#000",
                      boxShadow: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ display: "block", opacity: 0.6, mb: 0.5 }}
                    >
                      {new Date(msg.dateSend).toLocaleString()}
                    </Typography>

                    {msg.class === "image" ? (
                      <img
                        src={msg.msg}
                        alt="chat-img"
                        style={{
                          width: "100%",
                          borderRadius: "12px",
                          marginTop: "0.5rem",
                        }}
                      />
                    ) : (
                      <Typography variant="body2">{msg.msg}</Typography>
                    )}

                    {/* 🗨️ COLA TIPO WHATSAPP */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: isAgent ? "-8px" : "auto",
                        right: !isAgent ? "-8px" : "auto",
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: isAgent ? "8px solid #ffffff" : "none",
                        borderLeft: !isAgent ? "8px solid #dcf8c6" : "none",
                      }}
                    />
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography sx={{ textAlign: "center", marginTop: "2rem" }}>
              No hay mensajes en esta conversación.
            </Typography>
          )}
        </Modal>
      )}

      {isSurveyModalOpen && (
        <Modal
          visible={isSurveyModalOpen}
          onClose={() => setIsSurveyModalOpen(false)}
          title="Encuesta de Resolución"
          width="50vw"
          height="80vh"
          sx={{
            overflowY: "auto",
            padding: "1rem",
          }}
        >
          {showAlert && (
            <Box sx={{ width: "100%", marginBottom: "1rem" }}>
              <AlertComponent
                message={alertMessage}
                typeAlert={alertType}
                handleClose={hideAlert}
              />
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Tooltip title="Copiar encuesta">
              <IconButton onClick={handleCopySurvey}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>
          {isLoadingSurvey ? (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="inherit" />
            </Stack>
          ) : surveyData.length > 0 ? (
            surveyData.map((msg, index) => {
              const isAgent = msg.type === "agent";

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: isAgent ? "flex-start" : "flex-end",
                    width: "100%",
                    position: "relative",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "fit-content",
                      maxWidth: "75%",
                      padding: "0.8rem 1rem",
                      borderRadius: "18px",
                      backgroundColor: isAgent ? "#ffffff" : "#dcf8c6",
                      color: "#000",
                      boxShadow: 2,
                    }}
                  >
                    {msg.dateSend && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.6, mb: 0.5 }}
                      >
                        {new Date(msg.dateSend).toLocaleString()}
                      </Typography>
                    )}

                    <Typography variant="body2">{msg.msg}</Typography>

                    {/* 🗨️ COLA TIPO WHATSAPP */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: isAgent ? "-8px" : "auto",
                        right: !isAgent ? "-8px" : "auto",
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderRight: isAgent ? "8px solid #ffffff" : "none",
                        borderLeft: !isAgent ? "8px solid #dcf8c6" : "none",
                      }}
                    />
                  </Box>
                </Box>
              );
            })
          ) : (
            <Typography sx={{ textAlign: "center", marginTop: "2rem" }}>
              No hay datos para esta encuesta.
            </Typography>
          )}
        </Modal>
      )}

      {isImagesModalOpen && (
        <Modal
          visible={isImagesModalOpen}
          onClose={() => setIsImagesModalOpen(false)}
          title="Imágenes del Folio"
          width="70vw"
          height="80vh"
          sx={{
            overflowY: "auto",
            padding: "1rem",
          }}
        >
          {showAlert && (
            <Box sx={{ width: "100%", marginBottom: "1rem" }}>
              <AlertComponent
                message={alertMessage}
                typeAlert={alertType}
                handleClose={hideAlert}
              />
            </Box>
          )}
          {isLoadingImages ? (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="inherit" />
            </Stack>
          ) : images.length > 0 ? (
            <Stack
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "1rem",
              }}
            >
              {images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "0.5rem",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  <img
                    src={image.url}
                    alt={`Imagen ${index + 1}`}
                    style={{
                      width: "100%",
                      maxHeight: "300px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      marginBottom: "0.5rem",
                    }}
                  />
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {new Date(image.date).toLocaleString()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: "1rem",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  textAlign: "center",
                  color: "#666",
                }}
              >
                No hay imágenes cargadas por el ajustador
              </Typography>
            </Stack>
          )}
        </Modal>
      )}
    </Stack>
  );
}
export default OptionSearch;
